import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/d71v3qohb0rqicn68oqg';
const HERO_IMAGE = 'https://greenworldil.com/wp-content/uploads/2025/06/Tuckpointing.webp';
const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2968.3!2d-87.8059!3d41.9847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fcf594a21d73d%3A0x29c65c88f0ec9b5d!2s5801%20N%20Northwest%20Hwy%2C%20Chicago%2C%20IL%2060631!5e0!3m2!1sen!2sus!4v1710000000000';

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', city: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus('sending');
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <main>
      {/* Hero */}
      <section
        className="relative flex items-end min-h-64 pt-40 pb-16"
        style={{ backgroundImage: `url('${HERO_IMAGE}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/65 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-customer-service-2-line text-brand-gold text-xs" />
            <span className="text-brand-gold text-xs font-semibold uppercase tracking-widest">Contact Us</span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-3">
            Request Your Free<br />
            <span className="text-brand-gold">Roof Inspection</span>
          </h1>
          <p className="text-white/80 max-w-lg leading-relaxed">
            Get a free, no-obligation on-site inspection and a detailed written estimate. We respond within 1 business day.
          </p>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-brand-dark py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
          {['Free Inspections', 'Written Estimates', 'No Obligation', 'Same-Week Response'].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
              <i className="ri-shield-check-fill text-brand-gold text-xs" />
              {badge}
            </span>
          ))}
        </div>
      </section>

      {/* Main content */}
      <section className="bg-brand-cream py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Left: form */}
            <div className="lg:col-span-3">
              <h2 className="font-display text-3xl font-bold text-brand-dark mb-2">Schedule Your Inspection</h2>
              <p className="text-gray-600 text-sm mb-7">Fill out the form and we&apos;ll reach out to schedule your free on-site inspection at a time that works for you.</p>

              {status === 'success' ? (
                <div className="bg-white rounded-lg p-10 border border-brand-cream-3 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-brand-green/10 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-fill text-brand-green text-4xl" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-brand-dark mb-2">Request Received!</h3>
                  <p className="text-gray-600 text-sm">Thank you! We&apos;ll contact you within 1 business day to schedule your free inspection. In the meantime, call us at (708) 698-4072.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contact-page-form" className="bg-white rounded-lg p-8 border border-brand-cream-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1.5">Full Name *</label>
                      <input name="name" required value={formData.name} onChange={handleChange} className="w-full border border-brand-cream-3 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green" placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1.5">Phone Number *</label>
                      <input name="phone" required value={formData.phone} onChange={handleChange} type="tel" className="w-full border border-brand-cream-3 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green" placeholder="(708) 555-0000" />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-gray-700 block mb-1.5">Email Address *</label>
                    <input name="email" required value={formData.email} onChange={handleChange} type="email" className="w-full border border-brand-cream-3 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green" placeholder="your@email.com" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1.5">Service Needed</label>
                      <select name="service" value={formData.service} onChange={handleChange} className="w-full border border-brand-cream-3 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green bg-white">
                        <option value="">Select a service...</option>
                        <option>Flat Roofing</option>
                        <option>Shingle Roofing</option>
                        <option>Commercial Roofing</option>
                        <option>Tuckpointing &amp; Masonry</option>
                        <option>Chimney Repair</option>
                        <option>Sheet Metal &amp; Gutters</option>
                        <option>Vinyl Siding</option>
                        <option>Sealcoating &amp; Asphalt</option>
                        <option>Emergency Repair</option>
                        <option>Not Sure / General Inspection</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-700 block mb-1.5">Your City / Area</label>
                      <input name="city" value={formData.city} onChange={handleChange} className="w-full border border-brand-cream-3 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green" placeholder="e.g. Evanston, Oak Park..." />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-gray-700 block mb-1.5">Project Details</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={4} maxLength={500} className="w-full border border-brand-cream-3 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand-green resize-none" placeholder="Describe your roofing issue, project size, or any specific concerns..." />
                    <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'sending' || formData.message.length > 500}
                    className="w-full bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {status === 'sending' ? 'Sending Your Request...' : 'Request Free Inspection'}
                  </button>
                  {status === 'error' && (
                    <p className="text-red-500 text-xs text-center mt-3">Something went wrong. Please call us at (708) 698-4072.</p>
                  )}
                </form>
              )}
            </div>

            {/* Right: contact info + map */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Contact info card */}
              <div className="bg-brand-green rounded-lg p-7 text-white">
                <h3 className="font-display text-xl font-bold mb-5">Direct Contact</h3>
                <div className="flex flex-col gap-4">
                  <a href="tel:7086984072" className="flex items-start gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-phone-fill text-brand-gold text-base" />
                    </div>
                    <div>
                      <div className="text-xs text-white/60 mb-0.5">Call or Text</div>
                      <div className="font-bold">(708) 698-4072</div>
                    </div>
                  </a>
                  <a href="mailto:Greenworldroofing@gmail.com" className="flex items-start gap-3 cursor-pointer hover:opacity-80 transition-opacity">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-mail-fill text-brand-gold text-base" />
                    </div>
                    <div>
                      <div className="text-xs text-white/60 mb-0.5">Email Us</div>
                      <div className="font-semibold text-sm break-all">Greenworldroofing@gmail.com</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-2-fill text-brand-gold text-base" />
                    </div>
                    <div>
                      <div className="text-xs text-white/60 mb-0.5">Office Address</div>
                      <div className="text-sm font-semibold">5801 North Northwest Highway<br />Chicago, Illinois 60631</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-time-fill text-brand-gold text-base" />
                    </div>
                    <div>
                      <div className="text-xs text-white/60 mb-0.5">Business Hours</div>
                      <div className="text-sm font-semibold">Mon–Fri: 6:30AM – 6:00PM</div>
                      <div className="text-sm font-semibold">Saturday: 8:00AM – 2:00PM</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-lg overflow-hidden border border-brand-cream-3 h-52">
                <iframe src={MAP_EMBED} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Green World Construction location map" />
              </div>

              {/* Why contact us */}
              <div className="bg-white rounded-lg p-6 border border-brand-cream-3">
                <h3 className="font-semibold text-brand-dark mb-4 text-sm uppercase tracking-widest">What to Expect</h3>
                <ul className="flex flex-col gap-3">
                  {['Free on-site roof inspection', 'Detailed written estimate', 'No pressure, no obligation', 'Response within 1 business day', 'Licensed & insured crew', 'Transparent pricing — always'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-brand-green text-sm" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service areas reminder */}
      <section className="bg-brand-dark py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-gray-400 text-sm mb-3">Serving Chicago and Chicagoland including:</p>
          <p className="text-white/70 text-xs leading-relaxed max-w-3xl mx-auto">
            Evanston &bull; Skokie &bull; Oak Park &bull; Park Ridge &bull; Northbrook &bull; Berwyn &bull; Elmhurst &bull; Wheeling &bull; Elmwood Park &bull; Forest Park &bull; Franklin Park &bull; Norridge &bull; Barrington &bull; Cicero &bull; Burbank &bull; Harwood Heights &bull; Lincolnwood &bull; Melrose Park and more
          </p>
          <Link to="/locations" className="inline-flex items-center gap-1.5 text-brand-gold text-xs font-semibold mt-4 hover:underline cursor-pointer">
            View all service areas <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>
      </section>
    </main>
  );
}
